import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { bankAccountApi } from '../../services/bankAccountApi';

function normalizeIban(input: string): string {
  return input.replace(/\s+/g, '').toUpperCase();
}

export default function BankAccountForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [bankName, setBankName] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [iban, setIban] = useState('');
  const [branch, setBranch] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [order, setOrder] = useState<number>(0);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoading(true);
        const data = await bankAccountApi.getById(id!);
        setBankName(data.bankName);
        setAccountHolder(data.accountHolder);
        setIban(data.iban);
        setBranch(data.branch || '');
        setDescription(data.description || '');
        setIsActive(data.isActive);
        setOrder(data.order || 0);
      } catch (error) {
        toast.error('Banka hesabı yüklenemedi');
        navigate('/admin/bank-accounts');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!bankName.trim()) return toast.error('Banka adı gerekli');
    if (!accountHolder.trim()) return toast.error('Hesap sahibi gerekli');

    const normalizedIban = normalizeIban(iban);
    if (!/^TR\d{24}$/.test(normalizedIban)) {
      return toast.error('Geçerli bir TR IBAN giriniz (TR + 24 hane)');
    }

    const payload = {
      bankName: bankName.trim(),
      accountHolder: accountHolder.trim(),
      iban: normalizedIban,
      branch: branch.trim(),
      description: description.trim(),
      isActive,
      order: Number(order) || 0,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await bankAccountApi.update(id!, payload);
        toast.success('Banka hesabı güncellendi');
      } else {
        await bankAccountApi.create(payload);
        toast.success('Banka hesabı eklendi');
      }
      navigate('/admin/bank-accounts');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? 'Banka Hesabını Düzenle' : 'Yeni Banka Hesabı'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Banka Adı *</label>
          <input
            type="text"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="Örn: Ziraat Bankası"
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Hesap Sahibi *</label>
          <input
            type="text"
            value={accountHolder}
            onChange={(e) => setAccountHolder(e.target.value)}
            placeholder="Ad Soyad / Firma Ünvanı"
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">IBAN *</label>
          <input
            type="text"
            value={iban}
            onChange={(e) => setIban(e.target.value.toUpperCase())}
            placeholder="TR00 0000 0000 0000 0000 0000 00"
            className="w-full border rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-blue-500"
            required
          />
          <p className="text-xs text-gray-500 mt-1">TR ile başlayan 26 karakterli IBAN</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Şube (opsiyonel)</label>
          <input
            type="text"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Açıklama (opsiyonel)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Müşteriye gösterilecek not"
            rows={2}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Sıra</label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Küçük sıra önce gösterilir</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Durum</label>
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm">Aktif (müşteriye göster)</span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {saving ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Kaydet'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/bank-accounts')}
            className="px-6 py-2 rounded-lg border hover:bg-gray-50 transition"
          >
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}
